import React, { useCallback, useMemo, useState } from 'react';
import type { CampaignReporting } from 'c-entity-types';
import { AutoGrid, Button, CollapsibleCard, EntityAutocomplete, FlashMessage } from 'c-components';
import { Box } from '@mui/material';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { PermissionName } from 'c-sdk';
import { useEntityData } from 'c-data';
import type { TranslationPath } from 'c-translation';
import { Translate } from 'c-translation';
import { useDispatch, useSelector } from 'react-redux';
import { campaignReportingSelectors, campaignReportingThunks } from 'c-admin/Slices';
import { NetworkRequestState } from 'c-data-layer';
import UserEntityAutocomplete from 'c-admin/Components/User/UserEntityAutocomplete';

type Props = {
    title: TranslationPath;
    configId: number;
};

/**
 * Assign a saved configuration to users and the entities they belong to — the same shape as
 * skin assignment. Each row is gated on being able to read that entity type, so an admin only
 * sees the ones they can actually pick from.
 */
const ConfigUserAssign: React.FC<Props> = ({ title, configId }) => {
    const { hasAll } = useUserPermissions();
    const canEditAgencies = useMemo(
        () => hasAll([PermissionName.UniledportalAgencyRead]),
        [hasAll],
    );
    const canEditBuyers = useMemo(() => hasAll([PermissionName.UniledportalBuyerRead]), [hasAll]);
    const canEditClients = useMemo(() => hasAll([PermissionName.UniledportalClientRead]), [hasAll]);
    const canEditOwners = useMemo(() => hasAll([PermissionName.DisplaysOwnersRead]), [hasAll]);
    const canEditUsers = useMemo(() => hasAll([PermissionName.UniledportalUserRead]), [hasAll]);

    const { getById } = useEntityData<CampaignReporting>('CampaignReporting');
    const config = getById({ id: configId });

    const dispatch = useDispatch();
    const [agencies, setAgencies] = useState([...(config?.agencies ?? [])]);
    const [buyers, setBuyers] = useState([...(config?.buyers ?? [])]);
    const [clients, setClients] = useState([...(config?.clients ?? [])]);
    const [owners, setOwners] = useState([...(config?.owners ?? [])]);
    const [users, setUsers] = useState([...(config?.users ?? [])]);

    const { state, error } = useSelector(campaignReportingSelectors.getAttachingConfigById)(
        configId,
    );

    const saveAssignment = useCallback(() => {
        const data: Pick<
            CampaignReporting,
            'agencies' | 'buyers' | 'clients' | 'owners' | 'users'
        > = {};
        // Only send what this admin could actually edit — an omitted key leaves that
        // relation untouched server-side rather than clearing it.
        if (canEditAgencies) data.agencies = agencies;
        if (canEditBuyers) data.buyers = buyers;
        if (canEditClients) data.clients = clients;
        if (canEditOwners) data.owners = owners;
        if (canEditUsers) data.users = users;

        dispatch(campaignReportingThunks.attachCampaignReporting(configId, data));
    }, [
        agencies,
        buyers,
        canEditAgencies,
        canEditBuyers,
        canEditClients,
        canEditOwners,
        canEditUsers,
        clients,
        configId,
        dispatch,
        owners,
        users,
    ]);

    return (
        <CollapsibleCard title={title}>
            <AutoGrid spacing={2} xs={12} md={4} p={2}>
                {canEditAgencies && (
                    <Box>
                        <EntityAutocomplete
                            onChange={setAgencies}
                            multiple
                            value={agencies}
                            entityName="Agency"
                            textFieldProps={{ label: 'Agencies' }}
                            searchColumns="name"
                            labelColumn="name"
                        />
                    </Box>
                )}
                {canEditBuyers && (
                    <Box>
                        <EntityAutocomplete
                            onChange={setBuyers}
                            multiple
                            value={buyers}
                            entityName="Buyer"
                            textFieldProps={{ label: 'Buyers' }}
                            searchColumns="name"
                            labelColumn="name"
                        />
                    </Box>
                )}
                {canEditClients && (
                    <Box>
                        <EntityAutocomplete
                            onChange={setClients}
                            multiple
                            value={clients}
                            entityName="Client"
                            textFieldProps={{ label: 'Brands' }}
                            searchColumns="name"
                            labelColumn="name"
                        />
                    </Box>
                )}
                {canEditOwners && (
                    <Box>
                        <EntityAutocomplete
                            onChange={setOwners}
                            multiple
                            value={owners}
                            entityName="Displays_Owner"
                            textFieldProps={{ label: 'Media owners' }}
                            searchColumns="name"
                            labelColumn="name"
                        />
                    </Box>
                )}
                {canEditUsers && (
                    <Box>
                        <UserEntityAutocomplete
                            onChange={setUsers}
                            multiple
                            value={users}
                            textFieldProps={{ label: 'Users' }}
                        />
                    </Box>
                )}
            </AutoGrid>
            <Box mt={2} pl={2}>
                {error != null && <FlashMessage status="error">{error}</FlashMessage>}
                <Button
                    onClick={saveAssignment}
                    disabled={state === NetworkRequestState.InProgress}
                >
                    <Translate path="Modules.Admin.DashboardSetup.assignButton" />
                </Button>
            </Box>
        </CollapsibleCard>
    );
};

export default ConfigUserAssign;
